import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Ticket } from "lucide-react";
import { authFetch } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import AdminLoading from "../../components/admin/AdminLoading";
import AdminEmptyState from "../../components/admin/AdminEmptyState";
import AdminTable, { AdminMobileCard, adminTableHeadClass, adminTableRowClass, adminClickableRowClass } from "../../components/admin/AdminTable";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import { toast } from "sonner";
import {
  ADMIN_FIELD,
  ADMIN_LABEL,
  ADMIN_PANEL,
  ADMIN_SELECT,
  BTN_OUTLINE,
  BTN_PRIMARY,
} from "../../config/brand";

const emptyForm = {
  code: "",
  discount_type: "percentage",
  discount_value: 10,
  min_amount: "",
  max_discount: "",
  valid_from: "",
  valid_until: "",
  max_uses: "",
  description: "",
};

export default function AdminPromos() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch("/api/admin/promos");
      if (res.ok) setPromos(await res.json());
      else toast.error("Impossible de charger les codes promo");
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (promo) => {
    setEditing(promo);
    setForm({
      code: promo.code,
      discount_type: promo.discount_type,
      discount_value: promo.discount_value,
      min_amount: promo.min_amount ?? "",
      max_discount: promo.max_discount ?? "",
      valid_from: promo.valid_from ? promo.valid_from.slice(0, 10) : "",
      valid_until: promo.valid_until ? promo.valid_until.slice(0, 10) : "",
      max_uses: promo.max_uses ?? "",
      description: promo.description || "",
    });
    setErrors({});
    setDialogOpen(true);
  };

  const validate = () => {
    const next = {};
    if (!form.code.trim()) next.code = "Le code est requis";
    if (!form.discount_value || Number(form.discount_value) <= 0) {
      next.discount_value = "La valeur doit être supérieure à 0";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async () => {
    if (!validate()) return;

    setSaving(true);
    const payload = {
      code: form.code,
      discount_type: form.discount_type,
      discount_value: Number(form.discount_value) || 0,
      min_amount: form.min_amount === "" ? null : Number(form.min_amount),
      max_discount: form.max_discount === "" ? null : Number(form.max_discount),
      valid_from: form.valid_from || null,
      valid_until: form.valid_until || null,
      max_uses: form.max_uses === "" ? null : Number(form.max_uses),
      description: form.description || null,
    };

    try {
      const url = editing ? `/api/admin/promos/${editing.id}` : "/api/admin/promos";
      const method = editing ? "PUT" : "POST";
      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Erreur");
      }
      toast.success(editing ? "Code mis à jour" : "Code créé");
      setDialogOpen(false);
      load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await authFetch(`/api/admin/promos/${deleteTarget.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Erreur");
      toast.success("Code supprimé");
      setDeleteTarget(null);
      load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDeleting(false);
    }
  };

  const formatDiscount = (p) =>
    p.discount_type === "percentage" ? `-${p.discount_value}%` : `-${p.discount_value} €`;

  return (
    <div>
      <AdminPageHeader
        icon={Ticket}
        title="Codes promo"
        description="Gérer les codes de réduction"
        action={
          <Button onClick={openCreate} className={BTN_PRIMARY}>
            <Plus className="h-4 w-4 mr-2" />
            Nouveau code
          </Button>
        }
      />

      {loading ? (
        <AdminLoading message="Chargement des codes promo..." />
      ) : promos.length === 0 ? (
        <AdminEmptyState
          icon={Ticket}
          title="Aucun code promo"
          description="Créez un code de réduction pour vos clients."
          action={
            <Button onClick={openCreate} className={BTN_PRIMARY}>
              <Plus className="h-4 w-4 mr-2" />
              Nouveau code
            </Button>
          }
        />
      ) : (
        <AdminTable
          mobileCards={promos.map((p) => (
            <AdminMobileCard
              key={p.id}
              title={p.code}
              subtitle={formatDiscount(p)}
              onClick={() => openEdit(p)}
              badge={<Badge variant={p.active ? "default" : "secondary"}>{p.active ? "Actif" : "Inactif"}</Badge>}
              meta={[
                { label: "Validité", value: p.valid_until ? `jusqu'au ${p.valid_until.slice(0, 10)}` : "illimitée" },
                { label: "Usages", value: `${p.current_uses || 0}${p.max_uses != null ? ` / ${p.max_uses}` : ""}` },
              ]}
              actions={
                <>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(p)} aria-label="Modifier">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => setDeleteTarget(p)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              }
            />
          ))}
        >
          <Table>
            <TableHeader>
              <TableRow className={`${adminTableRowClass} hover:bg-transparent`}>
                <TableHead className={adminTableHeadClass}>Code</TableHead>
                <TableHead className={adminTableHeadClass}>Réduction</TableHead>
                <TableHead className={adminTableHeadClass}>Validité</TableHead>
                <TableHead className={adminTableHeadClass}>Usages</TableHead>
                <TableHead className={adminTableHeadClass}>Statut</TableHead>
                <TableHead className={`${adminTableHeadClass} text-right`}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {promos.map((p) => (
                <TableRow key={p.id} className={adminClickableRowClass} onClick={() => openEdit(p)}>
                  <TableCell className="text-slate-900 font-mono font-medium">
                    <span className="flex items-center gap-2">
                      <Ticket className="h-4 w-4 text-[#3a00e0]" />
                      {p.code}
                    </span>
                  </TableCell>
                  <TableCell className={ADMIN_LABEL}>{formatDiscount(p)}</TableCell>
                  <TableCell className="text-slate-400 text-sm">
                    {p.valid_until ? `jusqu'au ${p.valid_until.slice(0, 10)}` : "illimitée"}
                  </TableCell>
                  <TableCell className="text-slate-700 tabular-nums">
                    {p.current_uses || 0}
                    {p.max_uses != null ? ` / ${p.max_uses}` : ""}
                  </TableCell>
                  <TableCell>
                    <Badge variant={p.active ? "default" : "secondary"}>
                      {p.active ? "Actif" : "Inactif"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(p)} aria-label="Modifier">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => setDeleteTarget(p)}
                      aria-label="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AdminTable>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className={`${ADMIN_PANEL} text-slate-900 max-w-lg max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier le code" : "Nouveau code promo"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className={ADMIN_LABEL}>Code *</Label>
              <Input
                value={form.code}
                onChange={(e) => {
                  setForm({ ...form, code: e.target.value.toUpperCase() });
                  if (errors.code) setErrors({});
                }}
                placeholder="NOEL20"
                className={`${ADMIN_FIELD} font-mono ${errors.code ? "border-red-500" : ""}`}
              />
              {errors.code && <p className="text-red-600 text-xs">{errors.code}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={ADMIN_LABEL}>Type</Label>
                <select
                  value={form.discount_type}
                  onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                  className={`${ADMIN_SELECT}`}
                >
                  <option value="percentage">Pourcentage (%)</option>
                  <option value="fixed_amount">Montant fixe (€)</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label className={ADMIN_LABEL}>Valeur *</Label>
                <Input
                  type="number"
                  min={1}
                  value={form.discount_value}
                  onChange={(e) => {
                    setForm({ ...form, discount_value: e.target.value });
                    if (errors.discount_value) setErrors({});
                  }}
                  className={`${ADMIN_FIELD} ${errors.discount_value ? "border-red-500" : ""}`}
                />
                {errors.discount_value && <p className="text-red-600 text-xs">{errors.discount_value}</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={ADMIN_LABEL}>Montant min (€)</Label>
                <Input
                  type="number"
                  value={form.min_amount}
                  onChange={(e) => setForm({ ...form, min_amount: e.target.value })}
                  placeholder="optionnel"
                  className={ADMIN_FIELD}
                />
              </div>
              <div className="space-y-2">
                <Label className={ADMIN_LABEL}>Réduction max (€)</Label>
                <Input
                  type="number"
                  value={form.max_discount}
                  onChange={(e) => setForm({ ...form, max_discount: e.target.value })}
                  placeholder="optionnel (pour %)"
                  className={ADMIN_FIELD}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={ADMIN_LABEL}>Valide du</Label>
                <Input
                  type="date"
                  value={form.valid_from}
                  onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
                  className={ADMIN_FIELD}
                />
              </div>
              <div className="space-y-2">
                <Label className={ADMIN_LABEL}>Valide jusqu'au</Label>
                <Input
                  type="date"
                  value={form.valid_until}
                  onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                  className={ADMIN_FIELD}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={ADMIN_LABEL}>Usages max</Label>
                <Input
                  type="number"
                  value={form.max_uses}
                  onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                  placeholder="illimité"
                  className={ADMIN_FIELD}
                />
              </div>
              <div className="space-y-2">
                <Label className={ADMIN_LABEL}>Description (interne)</Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className={ADMIN_FIELD}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className={BTN_OUTLINE} disabled={saving}>
              Annuler
            </Button>
            <Button onClick={save} disabled={saving} className={BTN_PRIMARY}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Enregistrement...
                </>
              ) : (
                "Enregistrer"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}
        title="Supprimer ce code promo ?"
        description={`Le code « ${deleteTarget?.code} » sera définitivement supprimé.`}
        confirmLabel="Supprimer"
        onConfirm={confirmDelete}
        confirming={deleting}
      />
    </div>
  );
}
